import { useEffect } from 'react';

export const PROFILE_MENU_AUTO_CLOSE_MS = 5_000;

const PROFILE_MENU_SELECTOR = 'details.app-profile';
const PROFILE_MENU_ACTION_SELECTOR = '.app-profile__account, .app-profile__logout';
const DESKTOP_PROFILE_MENU_MEDIA = '(min-width: 701px)';

interface TimerHost {
  setTimeout(handler: () => void, timeout: number): number;
  clearTimeout(handle: number): void;
}

function elementFromTarget(target: EventTarget | null): Element | null {
  return target instanceof Element ? target : null;
}

function profileMenuFromTarget(target: EventTarget | null): HTMLDetailsElement | null {
  return elementFromTarget(target)?.closest<HTMLDetailsElement>(PROFILE_MENU_SELECTOR) ?? null;
}

function isDesktopProfileMenu(documentRef: Document): boolean {
  const view = documentRef.defaultView;
  if (!view || typeof view.matchMedia !== 'function') return true;
  return view.matchMedia(DESKTOP_PROFILE_MENU_MEDIA).matches;
}

/**
 * Branche le comportement commun du menu profil rendu par les différents shells Tosho.
 *
 * Le menu reste un élément natif `<details>` pour l'accessibilité et le fonctionnement sans
 * JavaScript spécifique au composant. Cette couche ne fait que compléter son cycle de vie : clic
 * extérieur, Échap, sélection d'une action et repli automatique après cinq secondes d'inactivité
 * sur les largeurs desktop. Toute interaction dans le menu repousse l'échéance.
 */
export function installProfileMenuAutoClose(
  documentRef: Document,
  timerHost: TimerHost = window,
): () => void {
  const timers = new Map<HTMLDetailsElement, number>();

  const clearTimer = (menu: HTMLDetailsElement): void => {
    const timer = timers.get(menu);
    if (timer === undefined) return;
    timerHost.clearTimeout(timer);
    timers.delete(menu);
  };

  const closeMenu = (menu: HTMLDetailsElement): void => {
    clearTimer(menu);
    menu.open = false;
  };

  const scheduleClose = (menu: HTMLDetailsElement): void => {
    clearTimer(menu);
    if (!menu.open || !isDesktopProfileMenu(documentRef)) return;
    timers.set(
      menu,
      timerHost.setTimeout(() => {
        timers.delete(menu);
        menu.open = false;
      }, PROFILE_MENU_AUTO_CLOSE_MS),
    );
  };

  const openMenus = (): HTMLDetailsElement[] =>
    Array.from(documentRef.querySelectorAll<HTMLDetailsElement>(`${PROFILE_MENU_SELECTOR}[open]`));

  const onToggle = (event: Event): void => {
    const menu = profileMenuFromTarget(event.target);
    if (!menu) return;
    if (menu.open) scheduleClose(menu);
    else clearTimer(menu);
  };

  const onPointerDown = (event: Event): void => {
    const activeMenu = profileMenuFromTarget(event.target);
    for (const menu of openMenus()) {
      if (menu !== activeMenu) closeMenu(menu);
    }
    if (activeMenu?.open) scheduleClose(activeMenu);
  };

  const onInteraction = (event: Event): void => {
    const menu = profileMenuFromTarget(event.target);
    if (menu?.open) scheduleClose(menu);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      for (const menu of openMenus()) closeMenu(menu);
      return;
    }
    onInteraction(event);
  };

  const onClick = (event: Event): void => {
    const element = elementFromTarget(event.target);
    if (!element?.closest(PROFILE_MENU_ACTION_SELECTOR)) return;
    const menu = profileMenuFromTarget(element);
    if (menu) closeMenu(menu);
  };

  documentRef.addEventListener('toggle', onToggle, true);
  documentRef.addEventListener('pointerdown', onPointerDown, true);
  documentRef.addEventListener('pointermove', onInteraction, true);
  documentRef.addEventListener('focusin', onInteraction, true);
  documentRef.addEventListener('keydown', onKeyDown, true);
  documentRef.addEventListener('click', onClick, true);

  return () => {
    documentRef.removeEventListener('toggle', onToggle, true);
    documentRef.removeEventListener('pointerdown', onPointerDown, true);
    documentRef.removeEventListener('pointermove', onInteraction, true);
    documentRef.removeEventListener('focusin', onInteraction, true);
    documentRef.removeEventListener('keydown', onKeyDown, true);
    documentRef.removeEventListener('click', onClick, true);
    for (const menu of timers.keys()) clearTimer(menu);
  };
}

export function ProfileMenuAutoClose(): null {
  useEffect(() => installProfileMenuAutoClose(document), []);
  return null;
}
